import { useCallback, useRef, useEffect, useId, useState } from 'react';
import {
  Box,
  Flex,
  IconButton,
  Button,
  Portal,
  Skeleton,
  SkeletonCircle,
  HStack,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';

import { RatingProgressDots } from './components/RatingProgressDots';
import { CategoryRatingStep } from './components/CategoryRatingStep';
import { RatingStep } from './components/RatingStep';
import { MenuItemsStep } from './components/MenuItemsStep';
import { CompletionStep } from './components/CompletionStep';
import { useRestaurantRating, isDiscardable } from './hooks/useRestaurantRating';
import { FOCUS_RING } from './ratingCategories';
import { visuallyHiddenSx, RATING_EMOJIS } from '@/utils/restaurantUtils';
import { useDiscardUploads } from '@/api/mutations/useRestaurantMutations';

const MotionBox = motion.create(Box);

interface RestaurantRatingModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Restaurant ID to rate */
  restaurantId: number;
  /** Restaurant display name */
  restaurantName?: string;
  /**
   * Open directly on the menu-items step (the profile's "Rate a menu item"
   * entry point). Only takes effect when the member has already rated all six
   * categories — otherwise the flow opens at the first category as usual. See
   * `useRestaurantRating`'s `startAtMenuItems`.
   */
  startAtMenuItems?: boolean;
}

/**
 * Restaurant Rating Modal — one-category-at-a-time rating booth, followed by
 * a menu items step and a completion screen.
 *
 * Animation architecture:
 * - The ratings booth is a plain Chakra Box (no Framer Motion at this level).
 *   It is toggled with CSS `display` (block/none) rather than AnimatePresence.
 *   This is intentional: AnimatePresence unmounts/remounts elements on exit,
 *   which destroys the WKWebView scroll position. Using display:none preserves
 *   the scroll position so that when the user returns from MenuItemsStep the
 *   booth is exactly where they left it (see goToRatings, which lands on the
 *   last category rather than resetting to the first).
 *   Category-to-category transitions WITHIN the booth are animated by
 *   RatingStep (AnimatePresence mode="popLayout" + drag, scoped to the card region
 *   only — see that component's doc comment).
 * - MenuItemsStep and CompletionStep are wrapped in AnimatePresence mode="wait".
 *   They slide in from the right on enter and slide out to the right on exit.
 *   mode="wait" ensures the exiting element fully animates out before the
 *   entering element mounts — this coordinates the display:none timing:
 *   goToRatings() sets activeView back to 'ratings' and AnimatePresence exits
 *   MenuItemsStep first, then React re-renders the booth as display:block.
 *
 * FocusLock:
 * - @chakra-ui/focus-lock is not available as a direct or transitive dependency
 *   in this project (confirmed via node_modules scan). This is a custom
 *   Portal-based sheet, not a Chakra Modal, so Chakra's internal FocusLock
 *   does not apply here. Focus management is handled manually: the close
 *   button receives focus on mount via autoFocus; focus moves to each new
 *   category's heading as the booth navigates; and focus returns to the
 *   Next button (or the current heading, if Next isn't rendered — mobile has
 *   no nav buttons) when navigating back from MenuItemsStep.
 */
export function RestaurantRatingModal({
  isOpen,
  onClose,
  restaurantId,
  restaurantName = '',
  startAtMenuItems = false,
}: RestaurantRatingModalProps) {
  const {
    activeView,
    goToRatings,
    goToCompletion,
    ratings,
    menuItems,
    addMenuItem,
    updateMenuItem,
    removeMenuItem,
    addMenuItemImage,
    removeMenuItemImage,
    categoryStep,
    currentCategory,
    isLastCategory,
    canGoNextCategory,
    canGoPrevCategory,
    swipeDirection,
    nudge,
    nudgeAnnounceKey,
    globalStep,
    goNextCategoryStep,
    goPrevCategoryStep,
    goToStep,
    setRatingAndAdvance,
    submit,
    isSubmitting,
    isLoading,
    loadError,
    onRetry,
    reset,
  } = useRestaurantRating({
    restaurantId,
    enabled: isOpen,
    startAtMenuItems,
  });

  /**
   * `initialized` guards against a flash of unrated emoji rows before the
   * hook's existing-data effect has applied cached ratings. `isLoading` is
   * only true on initial mount with no cached data; on a cached re-open the
   * data arrives synchronously (isLoading=false) so the skeleton never
   * shows at all. Ported from the deleted RatingsPage.
   */
  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    if (isLoading) setInitialized(false);
  }, [isLoading]);
  useEffect(() => {
    if (!isLoading) setInitialized(true);
  }, [isLoading, ratings]);
  const showSkeleton = isLoading || !initialized;

  const showNavButtons = useBreakpointValue({ base: false, md: true }, { ssr: false });
  const prefersReducedMotion = useReducedMotion();

  /**
   * Bounds the booth's content column on desktop so it doesn't stretch
   * edge-to-edge across the full viewport (mobile stays full-bleed, which is
   * intentional there). Reuses `showNavButtons`'s breakpoint rather than a
   * second `useBreakpointValue` call — same `md` threshold the pre-restore
   * version of this modal used for its `isMobile` flag.
   */
  const contentMaxW = showNavButtons ? '560px' : '100%';

  /**
   * Vertically centers the category card within the scrollable ratings area
   * on mobile only — a tall phone otherwise leaves the emoji row anchored to
   * the top of the screen, uncomfortably far from a one-handed thumb's
   * natural reach. Reuses the `showNavButtons`/`contentMaxW` breakpoint
   * convention: mobile-only (`base`), desktop (`md+`) keeps its existing
   * top-anchored layout unchanged, since thumb reach isn't a desktop
   * consideration.
   */
  const centerRatingContent = useBreakpointValue({ base: true, md: false }, { ssr: false });

  /**
   * Ref forwarded to the Next button. After returning from MenuItemsStep
   * (activeView transitions back to 'ratings'), focus is moved here in a
   * useEffect. We do NOT call .focus() synchronously because WKWebView
   * silently ignores .focus() calls on elements that are not yet
   * paint-visible (the display:none → display:block transition happens in
   * the same microtask as the state update). Deferring to
   * requestAnimationFrame ensures the element is paint-visible first.
   */
  const nextButtonRef = useRef<HTMLButtonElement>(null);

  const focusCurrentHeading = useCallback(() => {
    document.getElementById(`category-rating-heading-${currentCategory}`)?.focus();
  }, [currentCategory]);

  /** Track the previous view/step to distinguish "returned from menu items" from "navigated within the booth". */
  const prevActiveViewRef = useRef(activeView);
  const prevCategoryStepRef = useRef(categoryStep);
  useEffect(() => {
    const prevView = prevActiveViewRef.current;
    const prevStep = prevCategoryStepRef.current;
    prevActiveViewRef.current = activeView;
    prevCategoryStepRef.current = categoryStep;

    if (activeView === 'ratings' && prevView === 'menu_items') {
      requestAnimationFrame(() => {
        if (nextButtonRef.current) {
          nextButtonRef.current.focus();
        } else {
          focusCurrentHeading();
        }
      });
      return;
    }

    if (activeView === 'ratings' && prevView === 'ratings' && prevStep !== categoryStep) {
      requestAnimationFrame(focusCurrentHeading);
    }
  }, [activeView, categoryStep, focusCurrentHeading]);

  const { discardUploads } = useDiscardUploads();

  /**
   * Dual-purpose: the X button's onClick AND CompletionStep's onDone prop.
   * Applies uniformly to both — the status filter makes "closing after a
   * successful Finish" a no-op since linked images are never eligible for
   * discard. Discardable ids are captured from `menuItems` BEFORE reset()
   * empties it.
   */
  const handleClose = useCallback(() => {
    const discardableIds = menuItems.flatMap((item) =>
      item.pendingImages
        .filter((img) => isDiscardable(img.status))
        .map((img) => img.publicId)
    );
    if (discardableIds.length > 0) {
      discardUploads(discardableIds);
    }
    reset();
    onClose();
  }, [menuItems, reset, onClose, discardUploads]);

  /**
   * Passed as onFinish to MenuItemsStep. Calls submit(), which throws on
   * failure so the caller can handle the error inline and stay mounted. On
   * success, advances to the completion view.
   */
  const handleFinish = useCallback(async () => {
    await submit();
    goToCompletion();
  }, [submit, goToCompletion]);

  /**
   * Stable per-category onSelect for CategoryRatingStep — keyed on
   * currentCategory (setRatingAndAdvance itself is a stable reference). A
   * fresh function every render would defeat CategoryRatingStep's React.memo
   * regardless of its own prop comparison.
   */
  const handleSelect = useCallback(
    (value: number) => setRatingAndAdvance(currentCategory, value),
    [currentCategory, setRatingAndAdvance]
  );

  // Stable ID for the dialog's accessible name (aria-labelledby target)
  const headingId = useId();

  if (!isOpen) return null;

  return (
    <Portal>
      <MotionBox
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        // Translucent + blurred, matching the app-wide modal-overlay
        // convention (`<ModalOverlay bg="blackAlpha.700"
        // backdropFilter="blur(8px)" />`, used identically across 15+
        // modals — ReportMenuItemModal, CreateListModal, AddToListModal,
        // etc.). This is a deliberate frosted-glass effect: the guest sees
        // a soft, blurred hint of the `/search` page behind the rating
        // flow, not a flat opaque color.
        bg="blackAlpha.700"
        backdropFilter="blur(8px)"
        sx={{ WebkitBackdropFilter: 'blur(8px)' }}
        zIndex={1350}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        // Prevent click events from reaching elements behind the modal.
        // onTouchStart/onTouchEnd were removed — they called stopPropagation
        // on every touch across the entire modal, which was added to prevent
        // swipe-through during the old step navigation flow (now removed).
        // Broad touch interception interferes with scroll and tap targets
        // inside the modal on WKWebView. RatingStep's drag gesture is
        // deliberately scoped to just the card region for the same reason.
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        data-testid="rating-modal"
      >
        {/*
         * Visually-hidden heading — provides the accessible name for the
         * dialog. Not visible to sighted users; placed before the close
         * button in DOM order so screen readers announce it on dialog open.
         */}
        <Box id={headingId} sx={visuallyHiddenSx}>
          {restaurantName ? `Rate ${restaurantName}` : 'Rate restaurant'}
        </Box>

        {/* Close button — receives focus first via autoFocus on modal open */}
        <IconButton
          aria-label="Close rating modal"
          icon={<X size={24} />}
          position="absolute"
          top="calc(env(safe-area-inset-top, 0px) + 16px)"
          right={4}
          variant="ghost"
          color="gray.400"
          _hover={{ color: 'white', bg: 'whiteAlpha.200' }}
          _focusVisible={FOCUS_RING}
          onClick={handleClose}
          isDisabled={isSubmitting}
          zIndex={10}
          autoFocus
        />

        {/* Full-height layout container */}
        <Flex direction="column" h="100%" pt="calc(env(safe-area-inset-top, 0px) + 64px)">
          {/*
           * Progress dots — always mounted. Visually covered while
           * menu_items/completion are on top (those panels are
           * position="absolute" and cover the full modal), and
           * RatingProgressDots also strips the category dots' interactivity
           * (no button/tabIndex/click handler) once globalStep reaches
           * menu_items/completion, so neither a pointer click nor Tab+Enter
           * can yank the guest out of the completion screen.
           */}
          {restaurantName && (
            <Text
              color="gray.500"
              fontSize="sm"
              fontWeight="medium"
              mb={2}
              textAlign="center"
              px={4}
            >
              Rating {restaurantName}
            </Text>
          )}

          <Box px={4} pb={3}>
            <RatingProgressDots currentStep={globalStep} ratings={ratings} onStepClick={goToStep} />
          </Box>

          {/*
           * Ratings booth — always in the DOM; visibility toggled with CSS
           * display rather than mounting/unmounting. Preserves WKWebView
           * scroll position across the ratings ↔ menu_items transition.
           */}
          <Box
            display={activeView === 'ratings' ? 'flex' : 'none'}
            flexDirection="column"
            flex="1"
            overflow="hidden"
          >
            {showSkeleton && !loadError ? (
              <Box
                flex="1"
                overflowY="auto"
                aria-busy="true"
                aria-label="Loading ratings"
                px={4}
                pt={2}
              >
                <Skeleton height="60px" borderRadius="var(--r-card-compact)" mb={6} />
                <Skeleton height="24px" width="60%" mx="auto" mb={2} />
                <Skeleton height="16px" width="80%" mx="auto" mb={6} />
                <HStack spacing={3} justify="center">
                  {RATING_EMOJIS.map((emoji) => (
                    <SkeletonCircle key={emoji.value} size="12" />
                  ))}
                </HStack>
              </Box>
            ) : loadError ? (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                h="100%"
                px={6}
                gap={4}
                textAlign="center"
              >
                <Text fontSize="display">😕</Text>
                <Text color="white" fontWeight="semibold">
                  Couldn&apos;t load your ratings
                </Text>
                <Text color="gray.400" fontSize="sm">
                  Check your connection and try again.
                </Text>
                <Button
                  leftIcon={<RefreshCw size={16} />}
                  colorScheme="purple"
                  variant="outline"
                  onClick={onRetry}
                  size="md"
                  _focusVisible={FOCUS_RING}
                >
                  Try Again
                </Button>
              </Box>
            ) : (
              <Box
                display="flex"
                flexDirection="column"
                flex="1"
                overflow="hidden"
                w="100%"
                maxW={contentMaxW}
                mx="auto"
              >
                <Box
                  flex="1"
                  overflowY="auto"
                  px={4}
                  pt={2}
                  display={centerRatingContent ? 'flex' : undefined}
                  flexDirection={centerRatingContent ? 'column' : undefined}
                  sx={{ '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}
                >
                  {/*
                    `my="auto"` (not `justifyContent="center"` on the parent)
                    is the deliberate choice here: centering via auto margins
                    on the flex item degrades gracefully when the card is
                    taller than the available space — the margins collapse to
                    0 and the card scrolls into view from its top edge.
                    `justifyContent="center"` on an overflowing flex
                    container instead clips the top of the content, since the
                    scroll container can't scroll to negative offsets.
                  */}
                  <Box my={centerRatingContent ? 'auto' : undefined}>
                    <RatingStep
                      stepKey={currentCategory}
                      swipeDirection={swipeDirection}
                      onSwipeLeft={goNextCategoryStep}
                      onSwipeRight={goPrevCategoryStep}
                    >
                      <CategoryRatingStep
                        category={currentCategory}
                        value={ratings[currentCategory] ?? 0}
                        onSelect={handleSelect}
                      />
                    </RatingStep>
                  </Box>
                </Box>

                {/* Blocked-Next nudge — container always mounted, text toggled. */}
                <Box
                  role="status"
                  aria-live="polite"
                  minH="20px"
                  textAlign="center"
                  px={4}
                  color="orange.300"
                  fontSize="sm"
                >
                  <AnimatePresence mode="wait">
                    {nudge && (
                      <MotionBox
                        as="span"
                        key={nudgeAnnounceKey}
                        initial={prefersReducedMotion ? false : { opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: prefersReducedMotion ? 0 : 0.175 }}
                      >
                        Please rate this category first
                      </MotionBox>
                    )}
                  </AnimatePresence>
                </Box>

                {showNavButtons && (
                  <Box
                    borderTop="1px solid"
                    borderColor="whiteAlpha.200"
                    bg="gray.900"
                    px={4}
                    py={3}
                    display="flex"
                    alignItems="center"
                    gap={3}
                  >
                    <Button
                      variant="ghost"
                      size="lg"
                      color="gray.400"
                      onClick={goPrevCategoryStep}
                      isDisabled={!canGoPrevCategory}
                      leftIcon={<ChevronLeft size={20} />}
                      _hover={{ color: 'white', bg: 'whiteAlpha.100' }}
                      _focusVisible={FOCUS_RING}
                      data-testid="rating-prev"
                    >
                      Back
                    </Button>
                    <Button
                      ref={nextButtonRef}
                      colorScheme="purple"
                      size="lg"
                      flex={1}
                      onClick={goNextCategoryStep}
                      rightIcon={canGoNextCategory ? <ChevronRight size={20} /> : undefined}
                      _focusVisible={FOCUS_RING}
                      data-testid="rating-next"
                    >
                      {isLastCategory ? 'Rate Menu Items' : 'Next'}
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {/*
           * MenuItemsStep and CompletionStep slide in/out with AnimatePresence
           * mode="wait". The absolute positioning overlays the full modal
           * area; they sit above the ratings booth (which is display:none
           * while either of these views is active).
           */}
          <AnimatePresence mode="wait">
            {activeView === 'menu_items' && (
              <MotionBox
                key="menu_items"
                initial={{ x: '100%', opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: '100%', opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                pt="calc(env(safe-area-inset-top, 0px) + 64px)"
                display="flex"
                flexDirection="column"
              >
                <MenuItemsStep
                  menuItems={menuItems}
                  onAddItem={addMenuItem}
                  onUpdateItem={updateMenuItem}
                  onRemoveItem={removeMenuItem}
                  onAddMenuItemImage={addMenuItemImage}
                  onRemoveMenuItemImage={removeMenuItemImage}
                  onBack={goToRatings}
                  onFinish={handleFinish}
                  restaurantId={restaurantId}
                  restaurantName={restaurantName}
                />
              </MotionBox>
            )}

            {activeView === 'completion' && (
              <MotionBox
                key="completion"
                initial={{ x: '100%', opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: '100%', opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                pt="calc(env(safe-area-inset-top, 0px) + 64px)"
                pb="env(safe-area-inset-bottom, 0px)"
                overflowY="auto"
                sx={{
                  '&::-webkit-scrollbar': { display: 'none' },
                  scrollbarWidth: 'none',
                }}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <CompletionStep
                  ratings={ratings}
                  menuItems={menuItems}
                  onDone={handleClose}
                />
              </MotionBox>
            )}
          </AnimatePresence>
        </Flex>
      </MotionBox>
    </Portal>
  );
}

export default RestaurantRatingModal;
